import { z } from 'zod';
import { type TipoEventoPortal, TIPOS_PARTICIPANTE } from './campanha.js';
import { crescimentoClienteSchema } from './cliente.js';
import { voucherPainelSchema } from './voucher.js';

/**
 * Contrato do **portal do cliente** (`/v1/me/*`, role `cliente`, SPEC §8
 * "Portal do cliente" + `docs/UX-PORTAL-CLIENTE.md`).
 *
 * O portal **só lê** (CLAUDE.md regra 1): nenhuma regra de negócio nasce aqui.
 * Toda rota resolve o cliente por `req.usuario.clienteId` — nunca por parâmetro
 * de URL (SPEC §10, isolamento). Os campos de crescimento são **derivados** no
 * servidor, nunca coluna (CLAUDE.md regra 3): reusam `calcularCrescimentoCliente`,
 * a mesma lógica do `GET /v1/clientes/:id` do painel.
 */

/**
 * Resposta de `GET /v1/me`: os dados que o próprio cliente vê + os campos de
 * crescimento derivados (espalhados no topo, como o SPEC §8 lista). A `TrilhaBrindes`
 * desenha a régua a partir de `niveis[]` sem uma segunda requisição.
 */
export const meRespostaSchema = z
  .object({
    id: z.string().uuid(),
    nome: z.string(),
    primeiroNome: z.string(),
    telefone: z.string(),
    email: z.string().nullable(),
    tipoParticipante: z.enum(TIPOS_PARTICIPANTE),
    /** `true` = já era cliente da Varga: a Home não mostra "você foi indicado" (UX §4.6). */
    jaEraClienteVarga: z.boolean(),
    origem: z.enum(['organico', 'indicacao']),
    /** ISO 8601 */
    criadoEm: z.string(),
    /** código de indicação do próprio cliente (`CodigoIndicacao.codigo`). */
    codigo: z.string().nullable(),
    /** primeiro nome de quem indicou este cliente — `null` se veio orgânico (LGPD: só o primeiro nome). */
    indicadoPorPrimeiroNome: z.string().nullable(),
  })
  .merge(crescimentoClienteSchema);
export type MeResposta = z.infer<typeof meRespostaSchema>;

/** Resposta de `GET /v1/me/codigo` — o `CodigoDestaque` da Home. */
export const meCodigoRespostaSchema = z.object({ codigo: z.string() });
export type MeCodigoResposta = z.infer<typeof meCodigoRespostaSchema>;

/**
 * Voucher como o **cliente** vê (`GET /v1/me/vouchers`) — subconjunto de
 * `voucherPainelSchema` sem os campos internos (dono, retirante). `origem` é a
 * frase legível: "pela indicação de {primeiro nome}" ou "Brinde de boas-vindas"
 * (UX §5.4). `status` é derivado (`disponivel`/`entregue`); o voucher não expira.
 */
export const meVoucherSchema = voucherPainelSchema
  .pick({
    id: true,
    voucherCodigo: true,
    papel: true,
    nivel: true,
    descricao: true,
    status: true,
    concedidoEm: true,
    entregueEm: true,
  })
  .extend({ origem: z.string() });
export type MeVoucher = z.infer<typeof meVoucherSchema>;

export const meVouchersRespostaSchema = z.object({ itens: z.array(meVoucherSchema) });
export type MeVouchersResposta = z.infer<typeof meVouchersRespostaSchema>;

/**
 * Indicação como o cliente vê (`GET /v1/me/indicacoes`) — **só as convertidas**
 * (UX §5.5: rejeitadas NUNCA aparecem para o cliente, nem em agregado). Primeiro
 * nome do indicado + data + o brinde que a conversão rendeu.
 */
export const meIndicacaoSchema = z.object({
  primeiroNome: z.string(),
  /** ISO 8601 — `convertida_em` */
  convertidaEm: z.string(),
  /** descrição do brinde do indicador gerado por esta conversão */
  brindeDescricao: z.string().nullable(),
});
export type MeIndicacao = z.infer<typeof meIndicacaoSchema>;

export const meIndicacoesRespostaSchema = z.object({ itens: z.array(meIndicacaoSchema) });
export type MeIndicacoesResposta = z.infer<typeof meIndicacoesRespostaSchema>;

/**
 * Tipos de evento que o **portal** (cliente logado) dispara via `POST /v1/me/evento`
 * — contagem de engajamento dos estágios 2 (Ativação) e 3 (Share) do funil
 * (SPEC §4 EventoPortal, UX §10). `convite_visita` fica de fora: é contado no
 * servidor dentro de `GET /v1/publico/indicacao/:codigo`, sem nada no cliente.
 * Append-only, sem PII.
 */
export const TIPOS_EVENTO_PORTAL_CLIENTE = [
  'home_visita',
  'codigo_copiado',
  'share_iniciado',
  'share_fallback',
] as const satisfies ReadonlyArray<TipoEventoPortal>;
export type TipoEventoPortalCliente = (typeof TIPOS_EVENTO_PORTAL_CLIENTE)[number];

export const eventoPortalSchema = z.object({
  tipo: z.enum(TIPOS_EVENTO_PORTAL_CLIENTE),
});
export type EventoPortalInput = z.infer<typeof eventoPortalSchema>;
